/**
 * DE-768: hydrate cover swatch thumbs from SCAPI `c_pimlyProductImage`.
 * `c_allVariantsData.swatchImageUrl` is configuration (rectangle).
 */

import {useMemo} from 'react';
import {useQuery} from '@tanstack/react-query';
import {useAccessToken, useCommerceApi} from '@salesforce/commerce-sdk-react';

import {
  chunk,
  GET_COVER_VARIANT_VIEW_DETAILS_PARAMS,
  GET_PRODUCTS_PAGE_SIZE,
  GET_PRODUCTS_STALE_TIME_MS,
  mapWithConcurrency,
} from '@amblique/king-living/app/utils/api-utils';
import {mergeCoverSwatchPimlyProductImages} from '@amblique/king-living/app/utils/customize-selections';
import {uniqueProductIds} from '@amblique/king-living/app/utils/inventory-utils';

/**
 * @param {Object} options
 * @param {Array<Object>} options.variants
 * @param {boolean} [options.enabled=true]
 * @returns {{
 *   variants: Array<Object>,
 *   isPending: boolean,
 *   productsById: Record<string, Object>,
 * }}
 */
export function useCoverSwatchPimlyImages({variants, enabled = true}) {
  const api = useCommerceApi();
  const {getTokenWhenReady} = useAccessToken();
  const sourceVariants = Array.isArray(variants) ? variants : [];

  const ids = useMemo(() => {
    const unique = uniqueProductIds(
      sourceVariants.map((variant) => variant?.productId ?? variant?.id)
    );

    return unique.slice().sort();
  }, [sourceVariants]);

  const shouldFetch = Boolean(enabled) && ids.length > 0;
  const idsKey = ids.join(',');

  const {data, isLoading, isFetched} = useQuery({
    queryKey: ['king-living/cover-swatch-pimly-images', idsKey],
    queryFn: async () => {
      const token = await getTokenWhenReady();
      const chunks = chunk(ids, GET_PRODUCTS_PAGE_SIZE);
      // A full cover range is ~18 chunks. Firing them all at once fills the browser's
      // connection pool and delays the inventory call the Add to Cart button waits on.
      const results = await mapWithConcurrency(chunks, (chunkIds) => api.shopperProducts
        .getProducts({
          parameters: {
            ids: chunkIds.join(','),
            ...GET_COVER_VARIANT_VIEW_DETAILS_PARAMS,
          },
          headers: {Authorization: `Bearer ${token}`},
        })
        .then((res) => res?.data ?? []));

      return results.flat();
    },
    enabled: shouldFetch,
    staleTime: GET_PRODUCTS_STALE_TIME_MS,
    retry: 1,
  });

  const productsById = useMemo(() => {
    const map = {};

    (data || []).forEach((item) => {
      if (item?.id) {
        map[item.id] = item;
      }
    });

    return map;
  }, [data]);

  const hydrationComplete = !shouldFetch || isFetched;

  const hydratedVariants = useMemo(
    () => mergeCoverSwatchPimlyProductImages(sourceVariants, productsById, hydrationComplete),
    [sourceVariants, productsById, hydrationComplete]
  );

  return {
    variants: hydratedVariants,
    isPending: shouldFetch && isLoading,
    productsById,
  };
}

export default useCoverSwatchPimlyImages;
